import { jsxs, jsx } from "react/jsx-runtime";
import { styled } from "@linaria/react";
import { useRef, useState, useEffect, useLayoutEffect } from "react";
import { typographyClassName } from "@creditornot/cb-ingredients";
import "./style.css";
const TabItemContainer = /* @__PURE__ */ styled("div")({
  name: "TabItemContainer",
  class: "cb_Tabs_TabItemContainer_tdnv",
  propsAsIs: false
});
const LeftContent = /* @__PURE__ */ styled("div")({
  name: "LeftContent",
  class: "cb_Tabs_LeftContent_lpxb",
  propsAsIs: false
});
const RightContent = /* @__PURE__ */ styled("div")({
  name: "RightContent",
  class: "cb_Tabs_RightContent_rler",
  propsAsIs: false
});
const LabelContainer = /* @__PURE__ */ styled("div")({
  name: "LabelContainer",
  class: "cb_Tabs_LabelContainer_ler7",
  propsAsIs: false
});
const Label = /* @__PURE__ */ styled("span")({
  name: "Label",
  class: "cb_Tabs_Label_lrcc",
  propsAsIs: false
});
const LabelOverlay = /* @__PURE__ */ styled("span")({
  name: "LabelOverlay",
  class: "cb_Tabs_LabelOverlay_l19g",
  propsAsIs: false
});
const TabItem = /* @__PURE__ */ styled("button")({
  name: "TabItem",
  class: "cb_Tabs_TabItem_t1gy",
  propsAsIs: false
});
const TabItemContent = /* @__PURE__ */ styled("div")({
  name: "TabItemContent",
  class: "cb_Tabs_TabItemContent_t1ur",
  propsAsIs: false
});
const Root = /* @__PURE__ */ styled("div")({
  name: "Root",
  class: "cb_Tabs_Root_r19t",
  propsAsIs: false
});
const ActiveIndicator = /* @__PURE__ */ styled("div")({
  name: "ActiveIndicator",
  class: "cb_Tabs_ActiveIndicator_a189",
  propsAsIs: false
});
const Tabs = ({
  activeTabId,
  className,
  disabled,
  fixedWidths,
  items,
  onChange
}) => {
  const activeEl = useRef(null);
  const prevActiveTabId = useRef(activeTabId);
  const animationResetFallbackTimeoutId = useRef(null);
  const [direction, setDirection] = useState("right");
  const [forceActiveIndicatorRedraw, setForceActiveIndicatorRedraw] = useState(0);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [muteFocusIndicator, setMuteFocusIndicator] = useState(true);
  const [activeIndicatorState, setActiveIndicatorState] = useState({
    left: 0,
    right: "100%"
  });
  useEffect(() => {
    const prevId = prevActiveTabId.current;
    prevActiveTabId.current = activeTabId;
    if (prevId === activeTabId) {
      return;
    }
    const prevTabIndex = items.findIndex((tab) => tab.id === prevId);
    const activeTabIndex = items.findIndex((tab) => tab.id === activeTabId);
    setDirection(prevTabIndex <= activeTabIndex ? "right" : "left");
  }, [prevActiveTabId, activeTabId, items]);
  useLayoutEffect(() => {
    if (activeEl.current) {
      const {
        offsetLeft,
        offsetParent: parent,
        offsetWidth
      } = activeEl.current;
      if (!parent || !(parent instanceof HTMLElement))
        return;
      parent.scrollTo({
        left: offsetLeft - parent.clientWidth / 2 + offsetWidth / 2,
        behavior: isFirstRender ? "auto" : "smooth"
      });
    }
  }, [activeTabId, isFirstRender]);
  useLayoutEffect(() => {
    if (activeEl.current) {
      const {
        offsetLeft: left,
        offsetParent: parent,
        offsetWidth: width
      } = activeEl.current;
      if (!parent || !(parent instanceof HTMLElement))
        return;
      const right = parent.offsetWidth - left - width;
      setMuteFocusIndicator(true);
      setActiveIndicatorState({
        left,
        right
      });
      if (animationResetFallbackTimeoutId.current !== null) {
        clearTimeout(animationResetFallbackTimeoutId.current);
      }
      const timeoutId = setTimeout(() => {
        setMuteFocusIndicator(false);
      }, 300);
      animationResetFallbackTimeoutId.current = timeoutId;
      return () => {
        clearTimeout(timeoutId);
      };
    }
    return;
  }, [direction, activeTabId, fixedWidths, items, forceActiveIndicatorRedraw]);
  useLayoutEffect(() => {
    let unmounted = false;
    if (isFirstRender) {
      window.requestAnimationFrame(() => {
        if (!unmounted) {
          setIsFirstRender(false);
        }
      });
    }
    return () => {
      unmounted = true;
    };
  }, []);
  useEffect(() => {
    const resizeListener = () => {
      setForceActiveIndicatorRedraw(Date.now());
    };
    const opts = {
      passive: true
    };
    window.addEventListener("resize", resizeListener, opts);
    return () => {
      window.removeEventListener("resize", resizeListener, opts);
    };
  });
  return /* @__PURE__ */ jsxs(Root, {
    className,
    "data-fixed-widths": fixedWidths,
    children: [items.map((tab) => {
      const active = tab.id === activeTabId;
      return /* @__PURE__ */ jsx(TabItemContainer, {
        ref: active ? activeEl : null,
        children: /* @__PURE__ */ jsx(TabItem, {
          as: tab.component,
          className: typographyClassName.Body3,
          "data-active": active,
          "data-disabled": disabled ? disabled : void 0,
          "data-mute-focus-indicator": muteFocusIndicator,
          disabled: Boolean(disabled || tab.disabled),
          onClick: (event) => {
            event.currentTarget.focus();
            if (onChange) {
              onChange(tab.id);
            }
          },
          type: tab.component ? void 0 : "button",
          children: /* @__PURE__ */ jsxs(TabItemContent, {
            children: [!!tab.leftContent && /* @__PURE__ */ jsx(LeftContent, {
              children: tab.leftContent
            }), /* @__PURE__ */ jsxs(LabelContainer, {
              children: [/* @__PURE__ */ jsx(Label, {
                "data-show": !active,
                children: tab.label
              }), /* @__PURE__ */ jsx(LabelOverlay, {
                "data-show": active,
                children: tab.label
              })]
            }), !!tab.rightContent && /* @__PURE__ */ jsx(RightContent, {
              children: tab.rightContent
            })]
          })
        })
      }, tab.id);
    }), /* @__PURE__ */ jsx(ActiveIndicator, {
      "data-active": !isFirstRender,
      "data-direction": direction,
      onTransitionEnd: () => {
        if (animationResetFallbackTimeoutId.current !== null) {
          clearTimeout(animationResetFallbackTimeoutId.current);
        }
        setMuteFocusIndicator(false);
      },
      style: activeIndicatorState
    })]
  });
};
const Tabs$1 = Tabs;
export {
  Tabs$1 as Tabs
};
//# sourceMappingURL=index.js.map
